import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Dropdown } from 'antd'
import { UserOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import TranslationNS from '@/utils/Constants/TranslationNS'
import { AUTH_PATHS } from '@/components/Auth/constants'
import { useMin } from '@/hooks/useBreakpoint'
import useScrolled from '@/hooks/useScrolled'
import ScoringHorizontalSVG from '@/images/scoring-horizontal.svg?react'
import ScoringSVG from '@/images/scoring_icon.svg?react'
import GlobeSVG from '@/images/home/Globe.svg?react'
import BurgerMenuSVG from '@/images/BurgerMenu.svg?react'
import AvatarSVG from '@/images/home/Avatar.svg'
import ModalStripeInformations from './ModalStripeInformations'
import NavSearch from './NavSearch'
import NavbarMobile from './NavbarMobile'
import NavbarMobileMenu from './NavbarMobileMenu'
import useNavMenuItems, { LANGUAGES } from './useNavMenuItems'
import useNavLinksFit from './useNavLinksFit'
import { closeAdvancedSearch } from '@/utils/advancedSearchStore'
import { useAuth } from '../../middlewares/auth/auth-context'
import { getStripeInfo } from '../../middlewares/services/stripe.services'
import SButton from '../common/SButton'
import BookACallButton from './BookACallButton'
import ExternalLink from './ExternalLink'
import NewDesignChip from '../DesignTransition/NewDesignChip'

import './index.css'

// Kiosk routes: no navbar, no footer (App.js reuses this for the footer)
export const isPreviewPage = (path) => {
    const parts = path.split('/').filter((x) => x)
    if (['leaderboard', 'planning'].includes(parts[1]) && ['tv', 'stream'].includes(parts[2])) return true
    // Live Judging: only the big screen is a kiosk (like leaderboard/tv); the phone surfaces keep the site navbar
    if (parts[1] === 'live' && parts[2] === 'screen') return true
    return false
}

export default function Navbar({ processInProgress, setProcessInProgess }) {
    const { t, i18n } = useTranslation(TranslationNS.NAVBAR)

    const authContext = useAuth()
    const location = useLocation()

    const [userId] = useState(
        localStorage.getItem('athleteId') ? localStorage.getItem('athleteId') : localStorage.getItem('orgaId'),
    )
    const [bannerUpdateStripe, setBannerUpdateStripe] = useState(false)
    const [stripeAccount, setStripeAccount] = useState({})
    const [menuDropDownOpen, setMenuDropDownOpen] = useState(false)
    const isScrolled = useScrolled()

    // Switch desktop/mobile layouts at the `nav` breakpoint.
    const isDesktop = useMin('nav')
    const isMobile = !isDesktop

    // Shared menu model, consumed by the desktop dropdown and the mobile sheet.
    const menu = useNavMenuItems({ processInProgress, setProcessInProgess })

    // Desktop: "Our Services" / "Help & FAQ" stay in the bar whenever they fit; otherwise a burger takes
    // their place at the end of the bar and opens the site menu (the mobile sheet, which lists them).
    // If the bar still overflows with the burger, the username next to the avatar folds as well.
    const barRef = useRef(null)
    const linksRef = useRef(null)
    const burgerRef = useRef(null)
    const nameRef = useRef(null)
    const actionsRef = useRef(null)
    const fit = useNavLinksFit({ enabled: isDesktop, barRef, linksRef, burgerRef, nameRef, actionsRef })
    const linksFit = fit.links
    const [burgerOpen, setBurgerOpen] = useState(false)
    useEffect(() => {
        if (linksFit || !isDesktop) setBurgerOpen(false)
    }, [linksFit, isDesktop])

    // Desktop dropdown = the shared rows (kit: My Events / My Account / Disconnect — the
    // language lives in My Account). `open` is controlled so each row closes it itself.
    const closeDropdown = () => setMenuDropDownOpen(false)
    const dropdownItems = menu.profileMenuItems.map((it) =>
        it.onClick
            ? {
                  ...it,
                  onClick: () => {
                      closeDropdown()
                      it.onClick()
                  },
              }
            : it,
    )

    // Logged-out globe (kit #navGlobe): one click cycles to the next language.
    const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2)
    const cycleLanguage = () => {
        const index = LANGUAGES.findIndex(({ code }) => code === currentLanguage)
        i18n.changeLanguage(LANGUAGES[(index + 1) % LANGUAGES.length].code)
    }

    useEffect(() => {
        const getStripeAccountInfo = async () => {
            const response = await getStripeInfo()
            setStripeAccount(response.data.account)
        }

        const getUser = async () => {
            const userFound = authContext.user
            if (
                userFound.organisation_settings?.stripe?.account_id &&
                (!userFound.organisation_settings?.stripe || userFound.organisation_settings?.stripe?.account_id)
            )
                getStripeAccountInfo()

            if (userFound.organisation_settings?.stripe && !userFound.organisation_settings?.stripe.account_completed) {
                setBannerUpdateStripe(true)
            }
        }
        if (authContext.isLoggedIn && userId) {
            getUser()
        }
    }, [authContext, userId])

    // <Link to="/"> navigates to a bare '/', which clears the URL-driven home filters.
    const HandleBannerLinkClick = closeAdvancedSearch

    if (isPreviewPage(location.pathname)) return <div></div>

    // When the desktop auth modal is open, the page underneath is the one from
    // `state.backgroundLocation` — style the navbar for THAT page, not the URL.
    const backgroundLocation = location.state?.backgroundLocation
    const renderedPath =
        AUTH_PATHS.includes(location.pathname) && backgroundLocation ? backgroundLocation.pathname : location.pathname
    const isHome = renderedPath === '/'

    // `relative` (not `static`) so z-100 keeps the navbar above page content (the auth
    // modal overlay sits above it, kit: the nav is blurred under the modal).
    // Kit page-top: 35px on desktop, 20px under 760px (mobile layout here).
    return (
        <nav
            id="navbar"
            className={`
                ${isHome ? 'fixed' : 'relative'}
                w-full box-border flex items-center justify-center inset-0 bottom-auto z-100 mt-(--x001)
                transition-[padding,margin,width] duration-[180ms] ease-in-out motion-reduce:transition-none
                ${!isMobile && isScrolled && isHome ? 'p-0! m-0!' : ''}
                ${isMobile && !isHome ? 'mt-5!' : ''}
                ${isMobile && isHome ? `mt-0! ${isScrolled ? 'pt-0!' : 'pt-5!'}` : ''}
            `}
        >
            {bannerUpdateStripe && (
                <ModalStripeInformations
                    setBannerUpdateStripe={setBannerUpdateStripe}
                    user={authContext.user}
                    stripeAccount={stripeAccount}
                />
            )}

            {isDesktop ? (
                <div
                    ref={barRef}
                    className={`flex items-center gap-4 w-full h-[70px] px-6 bg-white border border-(--nav-bd) rounded-[10px] box-border transition-[max-width,border-radius,box-shadow,padding] ease-in-out duration-[180ms] motion-reduce:transition-none max-w-[min(calc(100%-var(--x003)),var(--x005))] mx-auto
                 ${
                     // Stuck state (Figma "Desktop Homepage + On Scroll Navbar"): full width,
                     // no rounding, no side borders, content aligned on the content column.
                     isScrolled && isHome
                         ? 'max-w-none rounded-none border-x-0 px-(--x006)! shadow-[0_6px_18px_rgba(14,16,23,.06)]'
                         : ''
                 }
                 `}
                >
                    <Link to="/" onClick={HandleBannerLinkClick} aria-label="Scoring.fit" className="s-focus-ring">
                        <ScoringHorizontalSVG
                            className={`
                            w-[135.2px] h-[27.76px] inline-flex items-center flex-none max-logo:hidden
                        `}
                        />
                        <ScoringSVG
                            className={`
                            h-[39px] w-[46px] inline-flex items-center flex-none logo:hidden
                        `}
                        />
                    </Link>

                    <NewDesignChip />

                    <NavSearch isLoggedIn={authContext.isLoggedIn} />

                    <div
                        ref={linksRef}
                        data-folded={linksFit ? undefined : ''}
                        className="nav-links flex items-center gap-[18px] ml-[26px] flex-none data-folded:hidden!"
                    >
                        <ExternalLink value={t('Our Services')} href="https://info.scoring.fit/" />
                        <ExternalLink value={t('Help & FAQ')} href="https://help.scoring.fit/" />
                    </div>

                    {/* ACTIONS */}
                    <div ref={actionsRef} className="flex items-center gap-(--x004) flex-none ml-auto">
                        {authContext.isLoggedIn ? (
                            <>
                                {/* "Create an Event" lives in the side menu (burger) and the home category bar,
                                    not in the bar itself (William 15 Oct 2026) */}
                                <Dropdown
                                    menu={{ items: dropdownItems }}
                                    trigger={['click']}
                                    placement="bottomRight"
                                    overlayClassName="navbar-desktop__dropdown mt-[9px]"
                                    open={menuDropDownOpen}
                                    onOpenChange={(open, info) => {
                                        // Row clicks close (or not) on their own — see dropdownItems.
                                        if (info?.source === 'menu') return
                                        setMenuDropDownOpen(open)
                                    }}
                                >
                                    <button
                                        type="button"
                                        className={`
                                    flex w-fit items-center gap-2 whitespace-nowrap
                                    border-0 bg-transparent p-0 m-0
                                    cursor-pointer appearance-none s-focus-ring
                                `}
                                    >
                                        <span
                                            ref={nameRef}
                                            data-folded={fit.name ? undefined : ''}
                                            className={`
                                            text-sm font-normal text-neutral-800/50
                                            ${menuDropDownOpen ? 'text-(--t-strong)!' : ''}
                                            max-wide:hidden data-folded:hidden!
                                        `}
                                        >
                                            {authContext.user.username}
                                        </span>

                                        <span
                                            className={`
                                        block size-[39px] shrink-0 overflow-hidden rounded-[10px] border border-(--nav-bd)
                                        ${menuDropDownOpen ? 'border-(--t-strong)!' : ''}
                                    `}
                                        >
                                            <img
                                                className="block size-full object-cover"
                                                src={authContext.user.logo || AvatarSVG}
                                                alt="Profile picture"
                                            />
                                        </span>
                                    </button>
                                </Dropdown>
                            </>
                        ) : (
                            <>
                                <Link
                                    to="/connect"
                                    state={
                                        AUTH_PATHS.includes(location.pathname)
                                            ? location.state
                                            : { backgroundLocation: location }
                                    }
                                >
                                    <SButton
                                        value={t('Log In')}
                                        className="h-[33px]! py-0! px-3.5! rounded-[5px]! font-normal! border bg-(--btn-bg)!"
                                        Icon={UserOutlined}
                                        iconLocation="left"
                                        iconStyle={{ fontSize: '1em' }}
                                    />
                                </Link>
                                {/* kit .nav-theme: 33px white square, --nav-bd border (styles in ./index.css) */}
                                <span className="navbar-desktop__lang inline-flex">
                                    <SButton
                                        value=""
                                        onClick={cycleLanguage}
                                        aria-label="Change language"
                                        title={currentLanguage.toUpperCase()}
                                        className="rounded-[5px]! font-normal!"
                                        Icon={GlobeSVG}
                                        iconLocation="left"
                                        iconStyle={{ fontSize: '1em' }}
                                    />
                                </span>

                                <span className="h-[26px] border-r border-(--nav-bd)" />

                                <BookACallButton t={t} />
                            </>
                        )}
                    </div>

                    {!linksFit && (
                        <button
                            ref={burgerRef}
                            type="button"
                            aria-label={t('Open menu')}
                            aria-expanded={burgerOpen}
                            onClick={() => setBurgerOpen(true)}
                            className="flex-none flex size-[38px] items-center justify-center bg-transparent border-0 p-0 cursor-pointer text-(--t-strong) s-focus-ring"
                        >
                            {/* same burger as the mobile bar (kit .nav-burger icon: 24px wide) */}
                            <BurgerMenuSVG className="w-6 h-auto" />
                        </button>
                    )}
                </div>
            ) : (
                <NavbarMobile
                    isHome={isHome}
                    stuck={isScrolled && isHome}
                    menu={menu}
                    processInProgress={processInProgress}
                    setProcessInProgess={setProcessInProgess}
                />
            )}

            {/* desktop burger → the site menu (NavbarMobile mounts its own for the mobile bar) */}
            {isDesktop && (
                <NavbarMobileMenu
                    open={burgerOpen}
                    onClose={() => setBurgerOpen(false)}
                    menu={menu}
                    processInProgress={processInProgress}
                    setProcessInProgess={setProcessInProgess}
                />
            )}
        </nav>
    )
}
